import SendIcon from '@mui/icons-material/Send';
import { Box, IconButton } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { postChatMessage } from '../../api/endpoints';
import CustomTextField from '../../components/CustomTextField';
import useIsSmallMobile from '../../custom-hooks/useIsSmallMobile';
import ChatBubble from './component/ChatBubble';
import CustomSlider from './component/CustomSlider';

type MessageType = {
  text: string;
  sender: 'You' | 'Assistant';
};

const ChatPage = () => {
  const isSmMobile = useIsSmallMobile();
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      const savedMessages = sessionStorage.getItem('chatMessages');
      if (savedMessages) {
        setMessages(JSON.parse(savedMessages));
      }
    } catch (error) {
      console.error('Failed to load saved messages:', error);
    }
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      try {
        sessionStorage.setItem('chatMessages', JSON.stringify(messages));
      } catch (error) {
        console.error('Failed to save messages:', error);
      }
    }
  }, [messages]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages]);

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || loading) return;

    const userMessage: MessageType = { sender: 'You', text: messageText };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    setIsTyping(true);
    setInput('');
    setError(null);

    try {
      const response = await postChatMessage(messageText);
      if (response) {
        const botMessage: MessageType = {
          sender: 'Assistant',
          text: response.answer || 'No response received',
        };
        setMessages((prev) => [...prev, botMessage]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
    } finally {
      setIsTyping(false);
      setLoading(false);
    }
  };

  const handleSend = () => {
    sendMessage(input);
  };

  const handleTagClick = (tag: string) => {
    const msg = `What did guests say about ${tag}?`;
    sendMessage(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: { xs: '100%', lg: '1200px' },
        mx: 'auto',
        overflowX: 'hidden',
      }}
    >
      <Box sx={{ mx: 'auto' }}>
        <Box sx={{ maxWidth: { xs: '100%', sm: '500px' }, my: 3 }}>
          <CustomSlider onClickPredefinedTag={handleTagClick} />
        </Box>
      </Box>
      <Box
        sx={{
          overflowY: 'scroll',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          width: { xs: isSmMobile ? '100%' : '90%', sm: '100%' },
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          height: '60vh',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box>
            {messages.map((msg, index) => (
              <ChatBubble key={index} sender={msg.sender} message={msg.text} />
            ))}
            {error && (
              <Box sx={{ color: 'error.main', textAlign: 'center', mt: 2 }}>Error: {error}</Box>
            )}
            {isTyping && <Box sx={{ textAlign: 'center', mt: 2 }}>Assistant is typing...</Box>}

            <div
              ref={messagesEndRef}
              style={{ height: '1px', width: '100%', marginBottom: '16px' }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: 'background.paper',
          paddingY: 1,
          borderTop: '1px solid rgba(0,0,0,0.1)',
          width: { xs: '90%', sm: '100%' },
        }}
      >
        <CustomTextField
          placeholder="Write your message..."
          isTextArea
          rows={4}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          type="text"
          onKeyDown={handleKeyDown}
          suffix={
            <IconButton
              onClick={handleSend}
              disabled={isTyping || !input.trim()}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                '&.Mui-disabled': {
                  cursor: 'not-allowed',
                  pointerEvents: 'auto',
                },
              }}
            >
              <SendIcon sx={{ color: '#ffffff' }} />
            </IconButton>
          }
        />
      </Box>
    </Box>
  );
};

export default ChatPage;
